import {ClickPlayer, ClickType} from "../../shared/click-player";
import {bindBooleanControl, bindNumericControl, makeBpmValueEditable, wireHintPopovers} from "../../shared/controls";
import {
    ClickSoundId,
    DEFAULT_DOWNBEAT_SOUND,
    DEFAULT_SECONDARY_SOUND,
    SoundSection,
    toClickSoundId,
} from "../../shared/sounds";
import {Tool} from "../../shared/tool";
import {ClickScheduler} from "./click-scheduler";
import {Settings} from "./settings";

/** Keys of Settings with numeric values. */
type NumericSettingKey = {
    [K in keyof Settings]: Settings[K] extends number ? K : never
}[keyof Settings];


/**
 * Clicker's composition module: owns its cards' control bindings and the
 * lazy audio construction (AudioContext created on first user gesture).
 * Extracted from the original Main class; behaviour unchanged.
 */
class ClickerTool implements Tool {

    private readonly settings: Settings;
    private audio: ClickPlayer | undefined;
    private scheduler: ClickScheduler | undefined;
    private playing = false;

    constructor() {
        this.settings = loadClickerSettings();
        this.initialise();
    }

    get isPlaying(): boolean {
        return this.playing;
    }

    /**
     * The player is created lazily, on the first user interaction that needs
     * audio (pressing play, or picking a sound in the Settings sheet), so
     * that the AudioContext is always created inside a user gesture.
     * @private
     */
    private getOrCreateAudio(): ClickPlayer {
        if (!this.audio) {
            const audio = new ClickPlayer(this.settings.downbeatSound, this.settings.secondarySound);
            this.audio = audio;

            this.scheduler = new ClickScheduler(this.settings, () => audio.currentTime);

            // rests and downbeats are already decided by the bar event stream;
            // the audio layer only maps them to a click type - no randomness here.
            this.scheduler.onsets$.subscribe(onset => {
                const clickType: ClickType = onset.event.isRest
                    ? 'silent'
                    : onset.event.isDownbeat
                        ? 'primary'
                        : 'secondary'

                audio.click(onset.timestamp, clickType);
            });
        }
        return this.audio;
    }

    private start() {
        this.playing = true;
        this.getOrCreateAudio()
            .resume()
            .then(() => this.scheduler?.start());
    }

    onSoundSelected(section: SoundSection, sound: ClickSoundId) {
        const key = section === 'downbeat' ? 'downbeatSound' : 'secondarySound';
        this.settings[key] = sound;
        localStorage.setItem(key, sound);

        const audio = this.getOrCreateAudio();
        if (section === 'downbeat') {
            audio.setPrimarySound(sound);
        } else {
            audio.setSecondarySound(sound);
        }
        if (!this.isPlaying) {
            void audio.preview(sound);
        }
    }

    stop() {
        this.playing = false;
        this.scheduler?.stop();
        this.audio?.stop();
        this.syncPlayControl();
    }

    /**
     * Wire up the UI listeners
     * @private
     */
    private initialise() {
        const playCheckbox = document.getElementById('play-checkbox') as HTMLInputElement;

        if (playCheckbox) {
            playCheckbox.addEventListener('click', (event) => {
                const isPlaying = (event.target as HTMLInputElement).checked;
                if (isPlaying) {
                    this.start();
                } else {
                    this.stop();
                }
                this.syncPlayControl();
            });
        }

        const bind = (id: string, key: NumericSettingKey) =>
            bindNumericControl(id, () => this.settings[key], value => {
                localStorage.setItem(key, '' + value);
                this.settings[key] = value;
            });

        bind('bpm', 'bpm');
        makeBpmValueEditable('bpm-output', 'bpm-input', 'bpm');
        bind('beatsPerBar', 'beatsPerBar');
        bind('granularity', 'granularity');
        bind('space', 'spaceBias');
        bind('tuplets', 'tupletBias');
        bindBooleanControl('hasDownbeatAlways', () => this.settings.hasDownbeatAlways, isChecked => {
            localStorage.setItem('hasDownbeatAlways', JSON.stringify(isChecked));
            this.settings.hasDownbeatAlways = isChecked;
        });

        wireHintPopovers(document.getElementById('tool-clicker')!);
    }

    /** Keep the play toggle's visual/aria state in sync with the playing state. */
    private syncPlayControl() {
        const playCheckbox = document.getElementById('play-checkbox') as HTMLInputElement;
        const playControl = playCheckbox?.closest('.play-control') as HTMLElement;
        if (!playCheckbox || !playControl) {
            return;
        }
        playCheckbox.checked = this.playing;
        playControl.classList.toggle('playing', this.playing);
        playControl.setAttribute('aria-label', this.playing ? 'Stop' : 'Play');
    }
}

/** Load Clicker's settings from localStorage (bare keys, unchanged). */
function loadClickerSettings(): Settings {
    return {
        bpm: +(localStorage.getItem('bpm') || 80),
        beatsPerBar: +(localStorage.getItem('beatsPerBar') || 4),
        granularity: +(localStorage.getItem('granularity') || 1),
        tupletBias: +(localStorage.getItem('tupletBias') || 1),
        spaceBias: +(localStorage.getItem('spaceBias') || 1),
        hasDownbeatAlways: (localStorage.getItem('hasDownbeatAlways') !== 'false'),
        downbeatSound: toClickSoundId(localStorage.getItem('downbeatSound'), DEFAULT_DOWNBEAT_SOUND),
        secondarySound: toClickSoundId(localStorage.getItem('secondarySound'), DEFAULT_SECONDARY_SOUND),
    };
}

export {ClickerTool};
